import { useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '~/hooks/useAppSelector';
import type { IUser } from '~/models/User';
import { apiMethodsConst } from '~/services/methods';
import { locationSettingsKeys } from './locationSettingsKeys';
import { selectKnownOffice } from './selectKnownOffice';

/**
 * After a location save: show the new values at once, then refetch the record.
 * Redux holds only the signed-in office and the selected one, so it is patched
 * only when the saved location is one of those; the sidebar switcher and the
 * app-wide popups read it.
 */
export const useApplyLocationUpdate = (officeId: string) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const reduxRecord = useAppSelector(selectKnownOffice(officeId));

  return (update: (office: IUser) => IUser) => {
    const cached = queryClient.getQueryData<IUser>(locationSettingsKeys.office(officeId));
    if (cached) {
      queryClient.setQueryData(locationSettingsKeys.office(officeId), update(cached));
    }
    queryClient.invalidateQueries({ queryKey: locationSettingsKeys.office(officeId) });

    if (reduxRecord) {
      dispatch({ type: apiMethodsConst.EDIT_USER_REQUEST, payload: update(reduxRecord) });
    }
  };
};
